/**
 * Package Status Hook
 * Custom hook for fetching and managing package status
 * Implements Issue #4: Package Cancellation UX Improvements
 */

import { useState, useEffect } from 'react'
import axios from '@/lib/api/axios-instance'
import { toast } from 'sonner'

interface PackageStatus {
  id: string
  name: string
  level: string
  price: number
  status: 'active' | 'cancelled_active' | 'expired' | 'suspended'
  credit_usage_frozen: boolean
  expires_at: string | null
  cancelled_at: string | null
  auto_renewal: boolean
  included_credits: number
  days_remaining: number | null
  is_expiring_soon: boolean
  can_renew_manually: boolean
  can_enable_auto_renewal: boolean
}

export function usePackageStatus() {
  const [packageStatus, setPackageStatus] = useState<PackageStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchPackageStatus = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // TODO: Replace with actual API endpoint when backend is ready
      const response = await axios.get('/api/customer/package/status')
      
      setPackageStatus(response.data.data)
    } catch (err: any) {
      // 404 = no package assigned yet, treat as null silently
      if (err.response?.status !== 404) {
        const message = err.response?.data?.error || 'ไม่สามารถโหลดข้อมูล package ได้'
        setError(message)
        toast.error(message)
      }
    } finally {
      setLoading(false)
    }
  }

  const renewPackage = async () => {
    try {
      setLoading(true)
      
      // TODO: Implement package renewal logic
      const response = await axios.post('/api/customer/package/renew')
      
      toast.success('ต่ออายุ package สำเร็จ')
      await fetchPackageStatus() // Refresh status
      
      return response.data
    } catch (err: any) {
      const message = err.response?.data?.error || 'ไม่สามารถต่ออายุ package ได้'
      toast.error(message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const enableAutoRenewal = async () => {
    try {
      setLoading(true)
      
      // TODO: Implement auto-renewal enable logic
      const response = await axios.post('/api/customer/package/auto-renewal/enable')
      
      toast.success('เปิดการต่ออายุอัตโนมัติสำเร็จ')
      await fetchPackageStatus() // Refresh status
      
      return response.data
    } catch (err: any) {
      const message = err.response?.data?.error || 'ไม่สามารถเปิดการต่ออายุอัตโนมัติได้'
      toast.error(message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const cancelPackage = async () => {
    try {
      setLoading(true)
      
      // TODO: Implement package cancellation logic
      const response = await axios.post('/api/customer/package/cancel')
      
      toast.success('ยกเลิก package สำเร็จ - คุณสามารถใช้งานได้จนถึงวันหมดอายุ')
      await fetchPackageStatus() // Refresh status
      
      return response.data
    } catch (err: any) {
      const message = err.response?.data?.error || 'ไม่สามารถยกเลิก package ได้'
      toast.error(message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPackageStatus()
  }, [])

  return {
    packageStatus,
    loading,
    error,
    refetch: fetchPackageStatus,
    renewPackage,
    enableAutoRenewal,
    cancelPackage
  }
}
